import React, { useState, useEffect, useCallback } from "react";
import { authFetch, apiUrl } from "../../utils/api";

/**
 * GitHub Backup card (Integrations) — the menu face of the portable backup.
 * Also fully drivable via Ollie ("set up my github backup").
 */
const GithubBackupCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const [oauthReady, setOauthReady] = useState(false);
  // null = unknown (endpoint didn't answer), true/false = explicit state
  const [tokenConnected, setTokenConnected] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await authFetch(apiUrl("/api/github-backup"));
      const data = await res.json();
      setStatus(data.status || null);
      if (typeof data.token_connected === "boolean") setTokenConnected(data.token_connected);
    } catch (_) { /* card stays in setup mode */ }
    try {
      const probe = await fetch(apiUrl("/api/oauth/github/configured"));
      setOauthReady((await probe.json()).configured === true);
    } catch (_) { /* PAT fallback copy shows */ }
    finally { setLoading(false); }
  }, []);

  const connectGithub = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await authFetch(apiUrl("/api/oauth/github/start"));
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || "Couldn't start GitHub connect");
      window.open(data.url, "_blank", "noopener");
      setMsg("Finish authorizing in the GitHub tab, then hit Set Up Backup.");
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [load]);

  const call = async (path, body) => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await authFetch(apiUrl(`/api/github-backup${path}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body || {}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Request failed");
      setMsg(data.first_sync ? `Repo ready — ${data.first_sync.files} files synced.` : data.result?.changed === false ? "Already up to date." : "Done.");
      await load();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const [importPreview, setImportPreview] = useState(null); // {repo, candidates} | null
  const [selected, setSelected] = useState({}); // path -> bool

  const checkImport = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await authFetch(apiUrl("/api/github-backup/import/preview"));
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Couldn't read the repo");
      setImportPreview(data);
      setSelected({});
      if (!data.candidates?.length) setMsg("Repo matches Optimus — nothing new to import.");
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const applyImport = async () => {
    const selections = (importPreview?.candidates || [])
      .filter((c) => selected[c.path])
      .map((c) => ({ path: c.path, sha: c.sha }));
    if (!selections.length) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await authFetch(apiUrl("/api/github-backup/import/apply"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ selections }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Import failed");
      setMsg(`Imported ${data.applied.length} item${data.applied.length === 1 ? "" : "s"}${data.skipped.length ? ` (${data.skipped.length} skipped)` : ""}.`);
      setImportPreview(null);
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const enabled = status && (status.enabled === true || status.enabled === 1);
  const chatsOn = status && (status.include_chats === true || status.include_chats === 1);
  const card = { background: "var(--bg-tertiary)", borderRadius: 12, padding: 16, marginBottom: 20 };
  const btn = { padding: "8px 14px", borderRadius: 8, border: "none", background: "var(--accent-primary)", color: "var(--text-on-accent)", fontWeight: 600, cursor: "pointer" };
  const btnGhost = { ...btn, background: "transparent", color: "var(--text-secondary)", border: "1px solid var(--border-primary)", fontWeight: 500 };

  return (
    <div className={`hf-panel${enabled ? " hf-connected" : ""}`} style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <h4 style={{ margin: 0 }}>
          🐙 GitHub Backup
          {/* Explicit connection state — "no signal" read as broken (Ashley BUG-5) */}
          {tokenConnected !== null && (
            <span
              style={{
                marginLeft: 10,
                fontSize: 12,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 999,
                background: tokenConnected ? "var(--accent-success-subtle, rgba(34,197,94,.15))" : "var(--bg-accent-subtle)",
                color: tokenConnected ? "var(--accent-success, #22c55e)" : "var(--text-secondary)",
              }}
            >
              {tokenConnected ? "✓ GitHub connected" : "Not connected"}
            </span>
          )}
        </h4>
        {enabled && status?.repo_full_name && (
          <a href={`https://github.com/${status.repo_full_name}`} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "var(--text-link)" }}>
            {status.repo_full_name} ↗
          </a>
        )}
      </div>
      <p style={{ color: "var(--text-secondary)", fontSize: 13, margin: "8px 0 12px" }}>
        A daily snapshot of your skills, dashboards, and workspace files into your <strong>own private repo</strong> — a
        portable backup your outside agents can work alongside. Credentials are redacted before every sync. Chats are
        excluded unless you opt in.
      </p>

      {loading ? (
        <p style={{ fontSize: 13 }}>Loading…</p>
      ) : !enabled ? (
        <>
        <div style={{ background: "var(--bg-accent-subtle)", border: "1px solid transparent", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.55 }}>
          <strong style={{ color: "var(--text-primary)" }}>Heads up — GitHub's permission screen will look scary.</strong>{" "}
          GitHub's sign-in only offers one permission level that can touch private repositories, so it asks
          for far more than we use. Here's our promise, enforced in code:{" "}
          <strong>we only ever touch the one backup repo we create for you</strong> — we never read your other
          repos, never delete anything, and we physically can't delete repos at all (we don't request that
          permission). Every sync re-verifies your backup repo is private before a single byte moves.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {oauthReady && (
            <button className="hf-press hf-focus" style={btnGhost} onClick={connectGithub} disabled={busy}>
              1. Connect GitHub
            </button>
          )}
          <button className="hf-press hf-focus" style={btn} onClick={() => call("/setup")} disabled={busy}>
            {busy ? "Setting up…" : oauthReady ? "2. Set Up Backup" : "Set Up Backup"}
          </button>
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            {oauthReady
              ? "Two clicks: authorize on GitHub, then set up."
              : "Requires your GitHub token (connect GitHub below first)."}
          </span>
        </div>
        </>
      ) : (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button style={btn} onClick={() => call("/run")} disabled={busy}>Sync Now</button>
          <button style={btnGhost} onClick={() => call("/chats", { include: !chatsOn })} disabled={busy}>
            {chatsOn ? "Exclude Chats" : "Include Chats"}
          </button>
          <button style={btnGhost} onClick={checkImport} disabled={busy}>
            Check Repo for Edits
          </button>
          <button style={btnGhost} onClick={() => window.confirm("Pause daily backups? Your repo stays yours.") && call("/disable")} disabled={busy}>
            Pause
          </button>
          <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
            {status?.last_synced_at ? `Last sync: ${new Date(status.last_synced_at).toLocaleString()}` : "Not synced yet"}
            {status?.last_error ? ` · ⚠️ ${status.last_error}` : " · syncs daily"}
          </span>
        </div>
      )}
      {importPreview?.candidates?.length > 0 && (
        <div style={{ marginTop: 14, borderTop: "1px solid var(--border-secondary)", paddingTop: 12 }}>
          <strong style={{ fontSize: 13 }}>
            Edits found in {importPreview.repo} — review before importing
          </strong>
          <p style={{ fontSize: 12, color: "var(--text-secondary)", margin: "6px 0 10px" }}>
            These were changed in your repo (by you or your outside agents). Only what you check gets
            pulled into Optimus — imported skills run in your conversations, so review anything you
            didn't write. Unchecked edits stay in the repo (the next daily sync overwrites them).
          </p>
          {importPreview.candidates.map((c) => (
            <label key={c.path} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "5px 0", fontSize: 13, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={!!selected[c.path]}
                onChange={(e) => setSelected((p) => ({ ...p, [c.path]: e.target.checked }))}
                style={{ marginTop: 3 }}
              />
              <span>
                {c.kind === "skill" ? "🧰" : "📄"} <strong>{c.name || c.path}</strong>{" "}
                <em style={{ color: "var(--text-secondary)" }}>
                  ({c.status === "new" ? "new" : "changed"} · {c.path} · {Math.max(1, Math.round(c.bytes / 1024))} KB)
                </em>
              </span>
            </label>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button style={btn} onClick={applyImport} disabled={busy || !Object.values(selected).some(Boolean)}>
              Import Selected
            </button>
            <button style={btnGhost} onClick={() => setImportPreview(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {msg && <p style={{ fontSize: 13, marginTop: 10, color: "var(--text-secondary)" }}>{msg}</p>}
    </div>
  );
};

export default GithubBackupCard;
